import { create } from 'zustand';
import { DEFAULT_FIELDS } from '../constants/fieldDefs';

export interface StatusEntry {
  done: boolean;
  note: string;
  updatedBy: string;
  updatedAt: string;
}

export interface CompanyStatusEntry {
  companyId: string | null;
  companyName: string | null;
  done: boolean;
  note: string;
  updatedBy: string;
  updatedAt: string;
}

export interface CallRecord {
  sessionIdentifier: string;
  callbackDone: boolean;
  callbackNote: string;
  callbackBy: string;
  callbackAt: string;
  callbackStatuses?: CompanyStatusEntry[]; // admin-only: all realms' entries
  [key: string]: unknown;
}

export type Period = 'TODAY' | 'THIS_MONTH' | 'LAST_MONTH';

interface FilterState {
  period: Period;
  count: number;
  fields: string[];
  missedOnly: boolean;
  search: string;
  // Admin-only: read-only durch die Brille dieses Unternehmens schauen (null = eigene/globale Sicht)
  viewAsCompanyId: string | null;
}

interface CallStore {
  calls: CallRecord[];
  loading: boolean;
  error: string | null;
  filter: FilterState;
  userName: string;
  darkMode: boolean;
  // WebSocket-Verbindungsstatus — für die "Live"-Anzeige in der Filterleiste
  wsConnected: boolean;
  setWsConnected: (v: boolean) => void;
  setCalls: (calls: CallRecord[]) => void;
  setLoading: (v: boolean) => void;
  setError: (e: string | null) => void;
  setFilter: (f: Partial<FilterState>) => void;
  setUserName: (name: string) => void;
  toggleDarkMode: () => void;
  applyStatuses: (statuses: Record<string, StatusEntry>) => void;
  updateStatus: (sessionId: string, patch: Partial<Pick<CallRecord, 'callbackDone' | 'callbackNote' | 'callbackBy' | 'callbackAt'>>) => void;
  updateCompanyStatus: (sessionId: string, entry: CompanyStatusEntry) => void;
  sessionVersion: number;
  bumpSessionVersion: () => void;
  // WS-Signal „neue Anrufe im Zeitraum X" — die Anruflisten-Ansicht lädt dann gezielt nach.
  callsUpdateSignal: { period: string; n: number } | null;
  signalCallsUpdate: (period: string) => void;
}

// Safety net: the upstream call API can return the same session twice. Duplicate
// sessionIdentifiers collide as React keys and make rows appear multiple times when a
// note/status changes — so collapse them here too, independent of the backend.
function dedupeBySession(calls: CallRecord[]): CallRecord[] {
  const seen = new Set<string>();
  const out: CallRecord[] = [];
  for (const c of calls) {
    const sid = c.sessionIdentifier;
    if (sid) {
      if (seen.has(sid)) continue;
      seen.add(sid);
    }
    out.push(c);
  }
  return out;
}

function loadUserName(): string {
  try { return localStorage.getItem('eves_username') ?? ''; } catch { return ''; }
}

function loadDarkMode(): boolean {
  try { return localStorage.getItem('eves_darkmode') === 'true'; } catch { return false; }
}

export const useCallStore = create<CallStore>((set) => ({
  calls: [],
  loading: false,
  error: null,
  sessionVersion: 0,
  bumpSessionVersion: () => set((s) => ({ sessionVersion: s.sessionVersion + 1 })),
  callsUpdateSignal: null,
  signalCallsUpdate: (period) =>
    set((s) => ({ callsUpdateSignal: { period, n: (s.callsUpdateSignal?.n ?? 0) + 1 } })),
  filter: { period: 'THIS_MONTH', count: 50, fields: DEFAULT_FIELDS, missedOnly: false, search: '', viewAsCompanyId: null },
  userName: loadUserName(),
  darkMode: loadDarkMode(),
  wsConnected: false,
  setWsConnected: (wsConnected) => set({ wsConnected }),
  setCalls: (calls) => set({ calls: dedupeBySession(calls) }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ error }),
  setFilter: (f) => set((s) => ({ filter: { ...s.filter, ...f } })),
  setUserName: (name) => {
    try { localStorage.setItem('eves_username', name); } catch { /* ignore */ }
    set({ userName: name });
  },
  toggleDarkMode: () =>
    set((s) => {
      const next = !s.darkMode;
      try { localStorage.setItem('eves_darkmode', String(next)); } catch { /* ignore */ }
      return { darkMode: next };
    }),
  applyStatuses: (statuses) =>
    set((s) => ({
      calls: s.calls.map((c) => {
        const entry = statuses[c.sessionIdentifier];
        if (!entry) return c;
        return {
          ...c,
          callbackDone: entry.done,
          callbackNote: entry.note,
          callbackBy: entry.updatedBy,
          callbackAt: entry.updatedAt,
        };
      }),
    })),
  updateStatus: (sessionId, patch) =>
    set((s) => ({
      calls: s.calls.map((c) =>
        c.sessionIdentifier === sessionId ? { ...c, ...patch } : c
      ),
    })),
  updateCompanyStatus: (sessionId, entry) =>
    set((s) => ({
      calls: s.calls.map((c) => {
        if (c.sessionIdentifier !== sessionId) return c;
        const existing = c.callbackStatuses ?? [];
        const updated = existing.some((cs) => cs.companyId === entry.companyId)
          ? existing.map((cs) => (cs.companyId === entry.companyId ? entry : cs))
          : [...existing, entry];
        return {
          ...c,
          callbackStatuses: updated,
          callbackDone: updated.some((cs) => cs.done),
          callbackNote: updated.map((cs) => cs.note).filter(Boolean).join(' | '),
        };
      }),
    })),
}));
